import apiClient from './api-client';

// Server Response Interfaces (based on actual API response)
export interface ServerDepartment {
    DepartmentId: number;
    ClientId: number;
    DepartmentName: string;
    Description?: string;
    IsActive: boolean;
    CreatedBy: number;
    CreatedDate: string;
    ModifiedDate?: string | null;
    ModifiedBy?: number | null;
}

export interface ServerDepartmentResponse {
    success: boolean;
    data: ServerDepartment[];
    pagination?: {
        total: number;
        page: number;
        pageSize: number;
        totalPages: number;
    };
}

// Client-side Interface (mapped from server response)
export interface Department {
    departmentId: number;
    clientId: number;
    departmentName: string;
    description?: string;
    isActive: boolean;
    createdBy: number;
    createdDate: string;
    modifiedDate?: string | null;
    modifiedBy?: number | null;
}

// Department Query Parameters
export interface DepartmentQueryParams {
    filters?: {
        isActive?: boolean;
    };
    pagination?: {
        page: number;
        pageSize: number;
    };
    sortBy?: 'a-z' | 'z-a' | 'newest' | 'oldest' | 'A-Z' | 'Z-A' | 'Newest' | 'Oldest';
}

// Service methods
export const departmentService = {
    getDepartments: async (queryParams?: DepartmentQueryParams): Promise<Department[]> => {
        const queryParamsObj = new URLSearchParams();
        
        if (queryParams?.filters) {
            if (queryParams.filters.isActive !== undefined) {
                queryParamsObj.append('isActive', queryParams.filters.isActive.toString());
            }
        }
        if (queryParams?.sortBy) {
            queryParamsObj.append('sortBy', queryParams.sortBy);
        }
        if (queryParams?.pagination) {
            queryParamsObj.append('page', queryParams.pagination.page.toString());
            queryParamsObj.append('pageSize', queryParams.pagination.pageSize.toString());
        }

        const response = await apiClient.get<ServerDepartmentResponse>(`/clients/departments?${queryParamsObj.toString()}`);
        
        // Map server response to client interface
        const mappedData: Department[] = response.data.map(serverDepartment => ({
            departmentId: serverDepartment.DepartmentId,
            clientId: serverDepartment.ClientId,
            departmentName: serverDepartment.DepartmentName,
            description: serverDepartment.Description,
            isActive: serverDepartment.IsActive,
            createdBy: serverDepartment.CreatedBy,
            createdDate: serverDepartment.CreatedDate,
            modifiedDate: serverDepartment.ModifiedDate,
            modifiedBy: serverDepartment.ModifiedBy
        }));

        return mappedData;
    },

    getDepartmentById: async (id: number): Promise<Department> => {
        const response = await apiClient.get<{ success: boolean; data: ServerDepartment }>(`/clients/departments/${id}`);
        
        const serverDepartment = response.data;
        return {
            departmentId: serverDepartment.DepartmentId,
            clientId: serverDepartment.ClientId,
            departmentName: serverDepartment.DepartmentName,
            description: serverDepartment.Description,
            isActive: serverDepartment.IsActive,
            createdBy: serverDepartment.CreatedBy,
            createdDate: serverDepartment.CreatedDate,
            modifiedDate: serverDepartment.ModifiedDate,
            modifiedBy: serverDepartment.ModifiedBy
        };
    },

    createDepartment: async (department: Omit<Department, 'departmentId' | 'clientId' | 'createdBy' | 'createdDate'>): Promise<Department> => {
        const response = await apiClient.post<{ success: boolean; data: ServerDepartment }>('/clients/departments', {
            DepartmentName: department.departmentName,
            Description: department.description,
            IsActive: department.isActive
        });
        
        const serverDepartment = response.data;
        return {
            departmentId: serverDepartment.DepartmentId,
            clientId: serverDepartment.ClientId,
            departmentName: serverDepartment.DepartmentName,
            description: serverDepartment.Description,
            isActive: serverDepartment.IsActive,
            createdBy: serverDepartment.CreatedBy,
            createdDate: serverDepartment.CreatedDate,
            modifiedDate: serverDepartment.ModifiedDate,
            modifiedBy: serverDepartment.ModifiedBy
        };
    },

    updateDepartment: async (id: number, department: Partial<Department>): Promise<Department> => {
        const response = await apiClient.put<{ success: boolean; data: ServerDepartment }>(`/clients/departments/${id}`, {
            DepartmentName: department.departmentName,
            Description: department.description,
            IsActive: department.isActive
        });
        
        const serverDepartment = response.data;
        return {
            departmentId: serverDepartment.DepartmentId,
            clientId: serverDepartment.ClientId,
            departmentName: serverDepartment.DepartmentName,
            description: serverDepartment.Description,
            isActive: serverDepartment.IsActive,
            createdBy: serverDepartment.CreatedBy,
            createdDate: serverDepartment.CreatedDate,
            modifiedDate: serverDepartment.ModifiedDate,
            modifiedBy: serverDepartment.ModifiedBy
        };
    },

    deleteDepartment: async (id: number): Promise<void> => {
        await apiClient.delete<{ success: boolean; message: string }>(`/clients/departments/${id}`);
    },

    updateDepartmentStatus: async (id: number, isActive: boolean): Promise<Department> => {
        const response = await apiClient.patch<{ success: boolean; data: ServerDepartment }>(`/clients/departments/${id}`, {
            IsActive: isActive
        });
        
        const serverDepartment = response.data;
        return {
            departmentId: serverDepartment.DepartmentId,
            clientId: serverDepartment.ClientId,
            departmentName: serverDepartment.DepartmentName,
            description: serverDepartment.Description,
            isActive: serverDepartment.IsActive,
            createdBy: serverDepartment.CreatedBy,
            createdDate: serverDepartment.CreatedDate,
            modifiedDate: serverDepartment.ModifiedDate,
            modifiedBy: serverDepartment.ModifiedBy
        };
    }
}; 